import {Color} from "@/lib/styles";
import ActionButton from "@/lib/components/ActionButton";
import Page from "@/lib/components/Page";
import {useMemo, useState} from "react";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import Message from "@/lib/components/Message";
import {useSession} from "@/lib/auth/auth";
import {AccountEditRequest} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {TextInput} from "react-native";
import globstyles from "@/lib/globstyles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useRouter} from "expo-router";
import {useAccount} from "@/lib/components/AccountProvider";
import {NotificationStatus, useNotification} from "@/lib/components/NotificationProvider";
import PrimaryText from "@/lib/components/PrimaryText";

export default function SettingsPage() {
    const {session, clearSession} = useSession();
    const router = useRouter();

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    // todo const [timezone, setTimezone] = useState<string>("");
    const [emailBusy, setEmailBusy] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [saveEmailError, setSaveEmailError] = useState<Error | null>(null);
    const [savePasswordError, setSavePasswordError] = useState<Error | null>(null);

    const {account, accountError, accountLoading, refreshAccount} = useAccount();
    const {notificationStatus, acquireNotificationToken} = useNotification();

    const notificationText = useMemo(() => {
        switch (notificationStatus) {
            case NotificationStatus.UNKNOWN:
                return "Ukjent";
            case NotificationStatus.BUSY:
                return "Sjekker...";
            case NotificationStatus.ACTIVE:
                return "Aktivert";
            case NotificationStatus.ERROR:
                return "Det skjedde en feil ved oppsett av varslinger."
            case NotificationStatus.DENIED:
                return "Blokkert i systeminnstillinger.";
            case NotificationStatus.UNAVAILABLE:
                return "Kan ikke aktiveres på denne enheten.";
        }
    }, [notificationStatus]);

    if (accountError) {
        return <ErrorPage error={accountError}/>
    } else if (accountLoading || !account) {
        return <SpinnerPage/>
    }

    async function saveSettings() {
        setEmailBusy(true);
        setSaveEmailError(null);
        setSavePasswordError(null);
        const changes: AccountEditRequest = {
            email: !!email ? email : "",
        };
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            setEmail("");
            await refreshAccount();
            router.dismiss();
        }).catch((error) => {
            setSaveEmailError(error);
        }).finally(() => {
            setEmailBusy(false);
        });
    }

    async function savePassword() {
        setPasswordBusy(true);
        setSaveEmailError(null);
        setSavePasswordError(null);
        const changes: AccountEditRequest = {
            password: !!password ? password : "",
        };
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            setPassword("");
            clearSession();
            router.replace(`/u`);
        }).catch((error) => {
            setSavePasswordError(error);
        }).finally(() => {
            setPasswordBusy(false);
        });
    }

    return (
        <Page scrollable={true}>
            <Header title={"Varslinger"}/>
            <Message text={notificationText}/>
            {(notificationStatus === NotificationStatus.UNKNOWN || notificationStatus === NotificationStatus.ERROR) &&
                <Nav>
                    <ActionButton
                        title={"Aktiver varslinger"}
                        onPress={() => acquireNotificationToken()}
                    />
                </Nav>
            }

            <Header title={"E-postadresse"}/>
            {account.email && <>
                <PrimaryText>Du har bekreftet mottak av e-post.</PrimaryText>
                <Message text={account.email}/>
            </>}

            {account.pending_emails.map(email => (
                <Message key={email}
                         text={"Vi har sendt en link til " + email + ".\n\nTrykk på linken for å bekrefte e-postadressen."}/>
            ))}

            <TextInput style={globstyles.formTextInput}
                       placeholder={"Bytt e-postadresse"}
                       placeholderTextColor={Color.FadedText}
                       value={email}
                       onChangeText={(value) => {
                           setEmail(value);
                           setSaveEmailError(null);
                       }}
                       inputMode={"email"}
                       textContentType={"emailAddress"}
                       autoCapitalize={"none"}
                       returnKeyType={"send"}
                       enablesReturnKeyAutomatically={true}
                       focusable={true}
                       keyboardAppearance={"dark"}
                       submitBehavior={"submit"}
                       onSubmitEditing={() => saveSettings()}
            />

            {saveEmailError && <ErrorMessage error={saveEmailError}/>}

            <Nav>
                <ActionButton title={"Send verifikasjonskode"} busy={emailBusy} color={Color.Green}
                              onPress={saveSettings}/>
            </Nav>

            <Header title={"Passord"}/>
            <TextInput style={globstyles.formTextInput}
                       placeholder={"Bytt passord"}
                       placeholderTextColor={Color.FadedText}
                       textAlignVertical={"center"}
                       value={password}
                       onChangeText={(value) => {
                           setPassword(value);
                           setSavePasswordError(null);
                       }}
                       inputMode={"text"}
                       textContentType={"newPassword"}
                       secureTextEntry={true}
                       returnKeyType={"send"}
                       enablesReturnKeyAutomatically={true}
                       focusable={true}
                       keyboardAppearance={"dark"}
                       submitBehavior={"submit"}
                       onSubmitEditing={() => savePassword()}
            />
            <Message
                text={"Ved å bytte passord blir kontoen din automatisk logget ut fra alle enheter, også den du bruker nå. Bytt passord hvis du mistenker at noen har fått tilgang til kontoen din."}/>

            {savePasswordError && <ErrorMessage error={savePasswordError}/>}

            <Nav>
                <ActionButton title={"Bytt passord"} busy={passwordBusy} color={Color.Green} onPress={savePassword}/>
            </Nav>

            <Header title={"Tidssone"}/>
            {account.email && <Message text={account.timezone}/>}

        </Page>
    );
}